import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
  useCallback,
  Fragment,
} from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import CacheBuster from "react-cache-buster";
import axios from "axios";
import appversion from "../package.json";
import Header from "./components/header";

const Home = lazy(() => import("./layout/home"));
const Post = lazy(() => import("./layout/post"));
const Search = lazy(() => import("./layout/Search"));
const PostCreator = lazy(() => import("./layout/postCreator"));
const Login = lazy(() => import("./layout/postCreator-login"));
const RoutesSplit = lazy(() => import("./components/routesSplit"));
const Footer = lazy(() => import("./components/Footer"));
const Widgets = lazy(() => import("./components/widgets"));

function App() {
  const [referral, setReferral] = useState(null);
  const [loanOfficerId, setLoanOfficerId] = useState(null);
  const [showOtherComponents, setShowOtherComponents] = useState(false);
  const [user, setUser] = useState(null);
  const appVersion = appversion.version;
  const isProduction = process.env.NODE_ENV === "production";
  const pathname = useLocation()?.pathname;
  const isHome = pathname === "/home" || pathname === "/home/";
  
  const handleScroll = useCallback(() => {
    const scrollPosition =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop;
    if (scrollPosition > 0) {
      setShowOtherComponents(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    // Clean up the scroll listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]); // Empty dependency array ensures the effect runs only once, like componentDidMount

  useEffect(() => {
    if (showOtherComponents) {
      window.removeEventListener("scroll", handleScroll);
    }
  }, [showOtherComponents, handleScroll]);

  useEffect(() => {
    const getLoanOfficerID = async () => {
      const localLoanOfficerId = sessionStorage.getItem("loanOfficerId");

      if (localLoanOfficerId && !referral) {
        setLoanOfficerId(localLoanOfficerId);
      } else if (referral) {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_LOAN_OFFICER_JSON}${referral}.json`
          );

          if (data?.loanOfficer?.ssid) {
            setLoanOfficerId(data.loanOfficer.ssid);
            sessionStorage.setItem("loanOfficerId", data.loanOfficer.ssid);
          } else {
            setLoanOfficerId("error");
          }
        } catch (err) {
          console.log(err);
          setLoanOfficerId("error");
        }
      }
    };
    getLoanOfficerID();
  }, [referral]);

  return (
    <CacheBuster
      currentVersion={appVersion}
      isEnabled={isProduction}
      isVerboseMode={false}
      metaFileDirectory={process.env.PUBLIC_URL}
    >
      <div className="App">
        <Header user={user} setUser={setUser} loanOfficerId={loanOfficerId} />
        <Routes>
          <Route
            exact
            path="/home"
            element={
              <Suspense fallback={<Fragment />}>
                <Home showOtherComponents={showOtherComponents} />
              </Suspense>
            }
          />
          <Route
            exact
            path="/search"
            element={
              <Suspense fallback={<Fragment />}>
                <Search />
              </Suspense>
            }
          />
          <Route
            exact
            path="/post-blog/post-creator"
            element={
              <React.Fragment>
                {user?.authorized ? (
                  <Suspense fallback={<Fragment />}>
                    <PostCreator />
                  </Suspense>
                ) : (
                  <Suspense fallback={<Fragment />}>
                    <Login setUser={setUser} />
                  </Suspense>
                )}
              </React.Fragment>
            }
          />
          <Route
            path="/:category/:title_id"
            element={
              <Suspense fallback={<Fragment />}>
                <Post showOtherComponents={showOtherComponents} />
              </Suspense>
            }
          />
          <Route
            path="/:path"
            element={
              <Suspense fallback={<Fragment />}>
                <RoutesSplit
                  loanOfficerId={loanOfficerId}
                  setReferral={setReferral}
                />
              </Suspense>
            }
          />
        </Routes>
        <Suspense fallback={<Fragment />}>
          <Widgets />
        </Suspense>
        {showOtherComponents || !isHome ? (
          <Suspense fallback={<Fragment />}>
            <Footer
              referral={referral}
              loanOfficerId={loanOfficerId}
              setLoanOfficerId={setLoanOfficerId}
            />
          </Suspense>
        ) : null}
      </div>
    </CacheBuster>
  );
}

export default App;
